import Alert from "@/components/alert/Alert";
import ChangesAlert from "@/components/alert/ChangesAlert";
import FormModal from "@/components/modals/FormModal";
import PaginationRow from "@/components/paginationRow/PaginationRow";
import { DELETE, GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Loader } from "@thdinc/hpf-shared";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import AddCampaignModal from "./AddCampaignModal";
import { Campaign } from "../interface";

interface CampaignsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type CampaignsView = "list" | "form";

const LIMIT = 10;

const CampaignsModal: React.FC<CampaignsModalProps> = ({ isOpen, onClose }) => {
    const { listCampaignsApi, campaignsApi } = API_ROUTES;

    const [view, setView] = useState<CampaignsView>("list");
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [hasNext, setHasNext] = useState<boolean>(false);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    useEffect(() => {
        if (isOpen) {
            setView("list");
            setEditingCampaign(null);
            setPageNumber(1);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || view !== "list") return;

        (async () => {
            try {
                setIsLoading(true);
                const query = new URLSearchParams({
                    limit: String(LIMIT),
                    pageNumber: String(pageNumber),
                });
                const result = await GET(`${listCampaignsApi}?${query.toString()}`);
                setCampaigns(result?.data?.result ?? []);
                setHasNext(Boolean(result?.data?.hasNext));
                setTotalResults(result?.data?.totalResults ?? 0);
            } catch (error) {
                console.log("get campaigns error:", error);
                Alert({ type: "error", message: "Unable to fetch campaigns" });
            } finally {
                setIsLoading(false);
            }
        })();
    }, [isOpen, view, pageNumber, listCampaignsApi, refreshKey]);

    const openAddCampaign = () => {
        setEditingCampaign(null);
        setView("form");
    };

    const openEditCampaign = (campaign: Campaign) => {
        setEditingCampaign(campaign);
        setView("form");
    };

    const confirmDelete = async () => {
        if (!deletingCampaign) return;
        try {
            setIsDeleting(true);
            const res = await DELETE(`${campaignsApi}/${deletingCampaign.id}`);
            Alert({ message: res?.data?.message ?? "Campaign deleted" });
            setDeletingCampaign(null);
            setPageNumber(1);
            setRefreshKey((key) => key + 1);
        } catch (error: any) {
            Alert({ type: "error", message: error?.response?.data?.message ?? "Unable to delete campaign" });
        } finally {
            setIsDeleting(false);
        }
    };

    const currentStart = totalResults === 0 ? 0 : (pageNumber - 1) * LIMIT + 1;
    const currentEnd = Math.min(pageNumber * LIMIT, totalResults);

    return (
        <>
            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                modalTitle={view === "form" ? (editingCampaign ? "Edit Campaign" : "Add Campaign") : "Campaigns"}
                width="lg:w-3/5 md:w-4/5 w-full"
                overflow="overflow-hidden"
            >
                {view === "list" ? (
                    <>
                        <div className="flex justify-end mb-3">
                            <button
                                type="button"
                                onClick={openAddCampaign}
                                className="bg-black text-white rounded-full py-2 px-5 text-sm font-medium whitespace-nowrap"
                            >
                                + Add Campaign
                            </button>
                        </div>

                        {isLoading ? (
                            <Loader color="bg-red-500" />
                        ) : campaigns.length > 0 ? (
                            <>
                                <div className="relative overflow-x-auto rounded-2xl overflow-y-auto max-h-full bg-white shadow-sm">
                                    <table className="w-full text-sm text-left rtl:text-right text-gray-600">
                                        <thead className="text-xs text-gray-400 uppercase tracking-wide">
                                            <tr>
                                                <th scope="col" className="px-2 sm:px-4 py-3">Name</th>
                                                <th scope="col" className="px-2 sm:px-4 py-3">Status</th>
                                                <th scope="col" className="px-2 sm:px-4 py-3">Points</th>
                                                <th scope="col" className="px-2 sm:px-4 py-3">Created</th>
                                                <th scope="col" className="px-2 sm:px-4 py-3">Link</th>
                                                <th scope="col" className="px-2 sm:px-4 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign) => (
                                                <tr
                                                    key={campaign.id}
                                                    className="border-t border-gray-100 last:border-b-0 hover:bg-p-gray/60 transition-colors"
                                                >
                                                    <td className="px-2 sm:px-4 py-4 min-w-32">{campaign.name}</td>
                                                    <td className="px-2 sm:px-4 py-4 min-w-24">
                                                        <span
                                                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${campaign.is_active
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-gray-100 text-gray-500"
                                                                }`}
                                                        >
                                                            {campaign.is_active ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-4 min-w-20 font-semibold text-gray-800">
                                                        {campaign.points}
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-4 min-w-28">
                                                        {format(parseISO(campaign.created_at), "MMM d, yyyy")}
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-4 min-w-24">
                                                        <a
                                                            href={campaign.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-primary-red underline hover:no-underline"
                                                        >
                                                            View
                                                        </a>
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-4 min-w-24">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => openEditCampaign(campaign)}
                                                                className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50"
                                                                aria-label={`Edit ${campaign.name}`}
                                                            >
                                                                <FiEdit size={14} className="text-gray-600" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDeletingCampaign(campaign)}
                                                                className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50"
                                                                aria-label={`Delete ${campaign.name}`}
                                                            >
                                                                <FiTrash2 size={14} className="text-primary-red" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <PaginationRow
                                    currentStart={currentStart}
                                    currentEnd={currentEnd}
                                    total={totalResults}
                                    disablePrev={pageNumber <= 1}
                                    disableNext={!hasNext}
                                    onPrev={() => setPageNumber((p) => Math.max(1, p - 1))}
                                    onNext={() => setPageNumber((p) => p + 1)}
                                />
                            </>
                        ) : (
                            <p className="text-center text-gray-500 text-lg mt-4">No campaigns found</p>
                        )}
                    </>
                ) : (
                    <AddCampaignModal
                        editingCampaign={editingCampaign}
                        onBack={() => setView("list")}
                        onSuccess={() => {
                            setPageNumber(1);
                            setRefreshKey((key) => key + 1);
                            setView("list");
                        }}
                    />
                )}
            </FormModal>

            <ChangesAlert
                title="Delete Campaign"
                desc={`Are you sure you want to delete "${deletingCampaign?.name}"? This cannot be undone.`}
                positiveButtonText="Delete"
                negativeButtonText="Cancel"
                isOpen={Boolean(deletingCampaign)}
                isLoading={isDeleting}
                handleSaveChanges={confirmDelete}
                handleDiscardChanges={() => setDeletingCampaign(null)}
            />
        </>
    );
};

export default CampaignsModal;
